import { useState, type FormEvent } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  ArrowLeft,
  ArrowRight,
  AtSign,
  Eye,
  EyeOff,
  KeyRound,
  ShieldCheck,
  Sparkles,
  UserRound,
} from "lucide-react";
import { useAuth } from "../../hooks/useAuth";
import { useSegmentedIndicator } from "../../hooks/useSegmentedIndicator";
import { COLLAPSE } from "../../lib/motion";
import { Brand } from "../ui/Brand";

type AuthTab = "login" | "register";

interface AuthScreenProps {
  /** Ferme l'écran : retour, « continuer en invité » et succès partagent la même sortie. */
  onClose: () => void;
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const DEFAULT_MIN_PASSWORD = 8;

const serverMessages: Record<string, string> = {
  ACCOUNTS_DISABLED: "Les comptes sont désactivés sur ce serveur. Tu peux jouer en invité.",
  EMAIL_TAKEN: "Un compte existe déjà avec cette adresse.",
  INVALID_CREDENTIALS: "E-mail ou mot de passe incorrect.",
  WEAK_PASSWORD: "Ce mot de passe est trop fragile, allonge-le un peu.",
  HANDLE_UNAVAILABLE: "Ce pseudo n’est plus disponible, essaie une variante.",
  VALIDATION_ERROR: "Certaines informations ne sont pas valides.",
  RATE_LIMITED: "Trop de tentatives. Patiente un instant avant de réessayer.",
  NETWORK_ERROR: "Le serveur est injoignable. Vérifie ta connexion.",
};

const errorField: Record<string, "email" | "password" | "displayName"> = {
  EMAIL_TAKEN: "email",
  WEAK_PASSWORD: "password",
  HANDLE_UNAVAILABLE: "displayName",
};

const strengthLabels = ["Trop court", "Fragile", "Correct", "Solide"];

const passwordStrength = (value: string, minimum: number) => {
  if (value.length < minimum) return 0;
  let score = 1;
  if (value.length >= minimum + 4) score += 1;
  if (/\d/.test(value) && /[a-zA-Z]/.test(value)) score += 1;
  if (/[^A-Za-z0-9]/.test(value)) score += 1;
  return Math.min(3, score);
};

export const AuthScreen = ({ onClose }: AuthScreenProps) => {
  const { serverConfig, accountsEnabled, error, pending, login, register, dismissError } = useAuth();
  const [tab, setTab] = useState<AuthTab>("login");
  const tabIndicator = useSegmentedIndicator<HTMLDivElement>(tab === "login" ? 0 : 1);
  const [email, setEmail] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [revealed, setRevealed] = useState(false);
  const [touched, setTouched] = useState(false);

  const minPassword = serverConfig?.minPasswordLength ?? DEFAULT_MIN_PASSWORD;
  const busy = pending === "login" || pending === "register";
  const isRegister = tab === "register";

  const emailValid = EMAIL_PATTERN.test(email.trim());
  const nameValid = displayName.trim().length >= 1 && displayName.trim().length <= 24;
  const passwordValid = isRegister ? password.length >= minPassword : password.length > 0;
  const confirmationValid = !isRegister || confirmation === password;
  const formValid =
    emailValid && passwordValid && (!isRegister || (nameValid && confirmationValid));

  const serverMessage = error ? serverMessages[error.code] ?? error.message : null;
  const serverField = error ? errorField[error.code] : undefined;
  const strength = passwordStrength(password, minPassword);

  const switchTab = (next: AuthTab) => {
    if (next === tab) return;
    setTab(next);
    setTouched(false);
    setConfirmation("");
    dismissError();
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setTouched(true);
    if (!formValid || busy || !accountsEnabled) return;
    const ok = isRegister
      ? await register({
          email: email.trim(),
          password,
          displayName: displayName.trim(),
        })
      : await login({ email: email.trim(), password });
    if (ok) onClose();
  };

  return (
    <main className="home-shell auth-shell">
      <header className="home-header">
        <Brand />
        <button className="ghost-button ghost-button--compact" type="button" onClick={onClose}>
          <ArrowLeft size={17} /> <span>Retour</span>
        </button>
      </header>

      <div className="auth-layout">
        <motion.section
          className="auth-panel glass-panel"
          initial={{ opacity: 0, y: 28, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          aria-labelledby="auth-title"
        >
          <div className="join-panel__halo" aria-hidden="true" />
          <span className="eyebrow">
            <Sparkles size={15} aria-hidden="true" />
            Compte Skyjo
          </span>
          <h1 id="auth-title">{isRegister ? "Crée ton compte" : "Content de te revoir"}</h1>
          <p className="auth-panel__lead">
            {isRegister
              ? "Garde ton pseudo, tes statistiques et ta liste d’amis d’une partie à l’autre."
              : "Retrouve tes amis, tes statistiques et tes invitations en cours."}
          </p>

          <div
            className="segmented-control"
            ref={tabIndicator.containerRef}
            role="tablist"
            aria-label="Connexion ou inscription"
          >
            {tabIndicator.indicator !== null && (
              <motion.span
                className="segmented-control__active"
                animate={{
                  x: tabIndicator.indicator.box.x,
                  y: tabIndicator.indicator.box.y,
                  width: tabIndicator.indicator.box.width,
                  height: tabIndicator.indicator.box.height,
                }}
                transition={
                  tabIndicator.indicator.animate
                    ? { type: "spring", stiffness: 420, damping: 36 }
                    : { duration: 0 }
                }
              />
            )}
            {(["login", "register"] as const).map((item) => (
              <button
                type="button"
                role="tab"
                aria-selected={tab === item}
                className={tab === item ? "is-active" : ""}
                onClick={() => switchTab(item)}
                key={item}
              >
                <span>{item === "login" ? "Connexion" : "Inscription"}</span>
              </button>
            ))}
          </div>

          <form className="auth-form" onSubmit={(event) => void submit(event)} noValidate>
            <label className="field-label" htmlFor="auth-email">Adresse e-mail</label>
            <div
              className={`text-field${(touched && !emailValid) || serverField === "email" ? " is-invalid" : ""}`}
            >
              <span className="text-field__sigil" aria-hidden="true"><AtSign size={17} /></span>
              <input
                id="auth-email"
                type="email"
                autoComplete="email"
                inputMode="email"
                spellCheck={false}
                maxLength={160}
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                onBlur={() => setTouched(true)}
                placeholder="camille@exemple.fr"
                aria-describedby={touched && !emailValid ? "auth-email-error" : undefined}
              />
            </div>
            {touched && !emailValid && (
              <p className="field-error" id="auth-email-error">Saisis une adresse e-mail valide.</p>
            )}
            {serverField === "email" && serverMessage && (
              <p className="field-error" role="alert">{serverMessage}</p>
            )}

            <AnimatePresence initial={false}>
              {isRegister && (
                <motion.div
                  className="auth-form__block"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={COLLAPSE}
                >
                  <label className="field-label" htmlFor="auth-name">Pseudo affiché</label>
                  <div
                    className={`text-field${(touched && !nameValid) || serverField === "displayName" ? " is-invalid" : ""}`}
                  >
                    <span className="text-field__sigil" aria-hidden="true"><UserRound size={17} /></span>
                    <input
                      id="auth-name"
                      autoComplete="username"
                      maxLength={24}
                      value={displayName}
                      onChange={(event) => setDisplayName(event.target.value)}
                      onBlur={() => setTouched(true)}
                      placeholder="Camille"
                      aria-describedby={touched && !nameValid ? "auth-name-error" : undefined}
                    />
                    <span className="text-field__count" aria-hidden="true">{displayName.length}/24</span>
                  </div>
                  {touched && !nameValid && (
                    <p className="field-error" id="auth-name-error">Choisis un pseudo de 1 à 24 caractères.</p>
                  )}
                  {serverField === "displayName" && serverMessage && (
                    <p className="field-error" role="alert">{serverMessage}</p>
                  )}
                  <p className="auth-hint">
                    Un numéro à quatre chiffres sera ajouté pour te distinguer, comme
                    {" "}<b>Camille#4207</b>.
                  </p>
                </motion.div>
              )}
            </AnimatePresence>

            <label className="field-label" htmlFor="auth-password">Mot de passe</label>
            <div
              className={`text-field${(touched && !passwordValid) || serverField === "password" ? " is-invalid" : ""}`}
            >
              <span className="text-field__sigil" aria-hidden="true"><KeyRound size={17} /></span>
              <input
                id="auth-password"
                type={revealed ? "text" : "password"}
                autoComplete={isRegister ? "new-password" : "current-password"}
                maxLength={128}
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                onBlur={() => setTouched(true)}
                placeholder={isRegister ? `Au moins ${minPassword} caractères` : "Ton mot de passe"}
                aria-describedby={touched && !passwordValid ? "auth-password-error" : undefined}
              />
              <button
                className="icon-button password-toggle"
                type="button"
                onClick={() => setRevealed((current) => !current)}
                aria-label={revealed ? "Masquer le mot de passe" : "Afficher le mot de passe"}
              >
                {revealed ? <EyeOff size={17} /> : <Eye size={17} />}
              </button>
            </div>
            {touched && !passwordValid && (
              <p className="field-error" id="auth-password-error">
                {isRegister
                  ? `Ton mot de passe doit contenir au moins ${minPassword} caractères.`
                  : "Saisis ton mot de passe."}
              </p>
            )}
            {serverField === "password" && serverMessage && (
              <p className="field-error" role="alert">{serverMessage}</p>
            )}

            {isRegister && password.length > 0 && (
              <div className="password-strength" aria-hidden="true">
                <span className="password-strength__bars" data-score={strength}>
                  <i /><i /><i />
                </span>
                <small>{strengthLabels[strength]}</small>
              </div>
            )}

            <AnimatePresence initial={false}>
              {isRegister && (
                <motion.div
                  className="auth-form__block"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={COLLAPSE}
                >
                  <label className="field-label" htmlFor="auth-confirmation">Confirme le mot de passe</label>
                  <div className={`text-field${touched && !confirmationValid ? " is-invalid" : ""}`}>
                    <span className="text-field__sigil" aria-hidden="true"><ShieldCheck size={17} /></span>
                    <input
                      id="auth-confirmation"
                      type={revealed ? "text" : "password"}
                      autoComplete="new-password"
                      maxLength={128}
                      value={confirmation}
                      onChange={(event) => setConfirmation(event.target.value)}
                      onBlur={() => setTouched(true)}
                      placeholder="Le même mot de passe"
                      aria-describedby={touched && !confirmationValid ? "auth-confirmation-error" : undefined}
                    />
                  </div>
                  {touched && !confirmationValid && (
                    <p className="field-error" id="auth-confirmation-error">Les deux mots de passe diffèrent.</p>
                  )}
                </motion.div>
              )}
            </AnimatePresence>

            {serverMessage && !serverField && (
              <p className="auth-alert" role="alert">{serverMessage}</p>
            )}

            <button
              className="primary-button primary-button--wide"
              type="submit"
              disabled={busy || !accountsEnabled}
            >
              {busy ? (
                <><span className="button-spinner" aria-hidden="true" /> {isRegister ? "Création…" : "Connexion…"}</>
              ) : (
                <>{isRegister ? "Créer mon compte" : "Me connecter"} <ArrowRight size={18} /></>
              )}
            </button>
          </form>

          <div className="auth-guest">
            <span aria-hidden="true" />
            <p>Le compte est optionnel</p>
            <span aria-hidden="true" />
          </div>
          <button className="secondary-button auth-guest__button" type="button" onClick={onClose}>
            Continuer en invité
          </button>
        </motion.section>
      </div>
    </main>
  );
};
